import {
  Box,
  Divider,
  Text,
  Flex,
  Switch,
  NumberInput,
  Tooltip,
} from '@mantine/core'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'

import { useFormContext } from './FormItem'

const SwitchWithTooltip = ({
  tooltipTitle,
  name,
  disabled,
  checked,
  onChange,
  toolTipDisabled = false,
}: {
  tooltipTitle: string | undefined
  name: string
  disabled: boolean
  checked: boolean
  onChange: (value: UNSAFE_any) => void
  toolTipDisabled?: boolean
}) => (
  <Tooltip
    label={tooltipTitle}
    disabled={toolTipDisabled}
    data-testid={`tooltip-${name}`}
  >
    <Box>
      <Switch
        disabled={disabled}
        checked={checked}
        onChange={onChange}
        data-testid={`switch-${name}`}
      />
    </Box>
  </Tooltip>
)

export function TabInventory() {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const form = useFormContext()
  const router = useRouter()

  const blocked_quantity = form.values?.blocked_quantity ?? 0
  const itemStatus = form.values.status
  const isTotalDisabled =
    itemStatus === 'disabled' || !form.values.track_quantity

  const tooltipTitle = useMemo(() => {
    if (itemStatus === 'disabled') {
      return t('inventory.enable_not_allowed')
    }

    return t('inventory.disable_not_allowed')
  }, [itemStatus])

  const isCreate = useMemo(() => {
    return router.pathname === '/items/create'
  }, [router])

  useEffect(() => {
    if (!isTotalDisabled && form.values.quantity === 0) {
      form.setFieldValue('quantity', 1)
    }
  }, [isTotalDisabled])

  useEffect(() => {
    const isDisabled = itemStatus === 'disabled'
    form.setFieldValue('on_store', !isDisabled)
    form.setFieldValue('track_quantity', !isDisabled)
    form.setFieldValue('allow_overbooking', !isDisabled)
  }, [itemStatus])

  return (
    <Box>
      <Box>
        <Text fw={500}>{t('inventory.title')}</Text>
        <Text c='dimmed'>{t('inventory.description')}</Text>
      </Box>
      <Divider bg='gray.5' my='lg' />
      <Flex
        align='center'
        justify='space-between'
        mb='md'
        data-testid='active-row'
      >
        <Flex direction='column'>
          <Text fw='500'>{t('inventory.item_active.title')}</Text>
          <Text c='dimmed'>{t('inventory.item_active.description')}</Text>
        </Flex>

        <SwitchWithTooltip
          tooltipTitle={tooltipTitle}
          name='active'
          disabled={blocked_quantity > 0}
          toolTipDisabled={blocked_quantity <= 0}
          checked={itemStatus === 'enabled'}
          onChange={(e) => {
            form.setFieldValue(
              'status',
              e.currentTarget.checked ? 'enabled' : 'disabled'
            )
          }}
        />
      </Flex>
      <Flex align='center' justify='space-between' data-testid='store-row'>
        <Flex direction='column'>
          <Text fw='500'>{t('inventory.on_store.title')}</Text>
          <Text c='dimmed'>{t('inventory.on_store.description')}</Text>
        </Flex>
        <Switch
          data-testid='switch-on_store'
          onChange={(e) => {
            form.setFieldValue('on_store', e.currentTarget.checked)
          }}
          checked={form.values.on_store}
        />
      </Flex>
      <Divider bg='gray.5' my='lg' />
      <Flex
        align='center'
        justify='space-between'
        mb='md'
        data-testid='track-row'
      >
        <Flex direction='column'>
          <Text fw='500'>{t('inventory.track_quantity.title')}</Text>
          <Text c='dimmed'>{t('inventory.track_quantity.description')}</Text>
        </Flex>

        <SwitchWithTooltip
          tooltipTitle={tooltipTitle}
          name='track_quantity'
          disabled={blocked_quantity > 0}
          toolTipDisabled={blocked_quantity <= 0}
          checked={form?.values?.track_quantity ?? false}
          onChange={(e) => {
            form.setFieldValue('track_quantity', e.currentTarget.checked)
          }}
        />
      </Flex>
      <Flex
        align='center'
        justify='space-between'
        mb='md'
        data-testid='overbooking-row'
      >
        <Flex direction='column'>
          <Text fw='500'>{t('inventory.allow_overbooking.title')}</Text>
          <Text c='dimmed'>{t('inventory.allow_overbooking.description')}</Text>
        </Flex>

        <SwitchWithTooltip
          tooltipTitle={tooltipTitle}
          toolTipDisabled={!form.values.track_quantity || blocked_quantity <= 0}
          name='allow_overbooking'
          disabled={blocked_quantity > 0 || !form.values?.track_quantity}
          checked={form.values.allow_overbooking ?? false}
          onChange={(e) => {
            form.setFieldValue('allow_overbooking', e.currentTarget.checked)
          }}
        />
      </Flex>
      <Flex direction='column' data-testid='total-row'>
        <NumberInput
          size='sm'
          w='25%'
          label={t('inventory.total.title')}
          min={1}
          mb='md'
          disabled={isTotalDisabled}
          {...form.getInputProps('quantity')}
        />
        {!isCreate && (
          <Flex align='center' direction='row'>
            <Box c='green' size='sm'>
              {t('inventory.total.you_have')}{' '}
              <Text c='dark' fw='500' size='sm' display='inline'>
                {blocked_quantity}
              </Text>{' '}
              {t('inventory.total.items_in_use')}
            </Box>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}
